import { useRef, useState } from 'react';

import { getFileIdFromName, UploadedFile } from 'pkg/components/FileUploader/UploadedFile';
import { fileUploaderService } from 'pkg/components/FileUploader/fileUploaderService';
import { getDiffBetweenArrays } from 'pkg/utils/getDiffBetweenArrays';

const addOrUpdate = (file: UploadedFile, oldFiles: UploadedFile[]) => {
  const index = oldFiles.findIndex((oldFile) => oldFile.id === file.id);
  if (index >= 0) {
    return oldFiles.map((currentFile, arrayIndex) => (arrayIndex === index ? file : currentFile));
  }
  return [...oldFiles, file];
};

const mapStringsToUploadedFiles = (files: string[]): UploadedFile[] =>
  files.map((name) => ({
    id: getFileIdFromName(name),
    isUploaded: true,
    file: { name } as File,
    progress: 100,
    controller: new AbortController(),
  }));

export const useFileUploader = ({ onChange, value }: { onChange: (files: string[]) => void; value: string[] }) => {
  const filesToRemove = useRef<UploadedFile[]>([]);
  const [submittedFiles, setSubmittedFiles] = useState<UploadedFile[]>(() => mapStringsToUploadedFiles(value));
  const [files, setFiles] = useState<UploadedFile[]>(() => mapStringsToUploadedFiles(value));

  const removeFile = (file: UploadedFile) => {
    if (!file.isUploaded) {
      file.controller.abort();
    }
    filesToRemove.current.push(file);
    setFiles((oldFiles) => oldFiles.filter(({ id }) => id !== file.id));
  };

  const revertFiles = async () => {
    getDiffBetweenArrays<UploadedFile>(files, submittedFiles).forEach(removeFile);
    getDiffBetweenArrays<UploadedFile>(filesToRemove.current, submittedFiles).forEach(fileUploaderService.deleteFile);
    filesToRemove.current = [];
    setFiles(submittedFiles);
  };

  const submitFiles = () => {
    filesToRemove.current.forEach(fileUploaderService.deleteFile);
    filesToRemove.current = [];
    setSubmittedFiles(files);
    onChange(files.map(({ file }) => file.name));
  };

  const addFiles = async (newFiles: File[]) => {
    await fileUploaderService.uploadFiles(
      newFiles.map((file) => ({
        file,
        onProgress: (uploadedFile) => setFiles((oldFiles) => addOrUpdate(uploadedFile, oldFiles)),
      }))
    );
  };

  const clearFiles = async () => {
    await [...files, ...submittedFiles].forEach((file) => fileUploaderService.deleteFile(file));
    setFiles([]);
  };

  return {
    files,
    addFiles,
    removeFile,
    clearFiles,
    revertFiles,
    submitFiles,
    loading: files.some((file) => !file.isUploaded),
  };
};
